import type { FunctionsHttpError } from "@supabase/supabase-js";

/**
 * Extract the real error payload from a `sync-bol-s3` invocation.
 *
 * The Supabase Functions JS client wraps non-2xx responses in a generic
 * `FunctionsHttpError: Edge Function returned a non-2xx status code` and
 * does NOT parse the JSON body into `data`. The actual `{ error, code }`
 * envelope only lives on `error.context.response` and must be read out
 * manually. Without this, every backend failure surfaces in the UI as the
 * useless "returned a non-2xx status code" string and the structured
 * code (e.g. `STS_ASSUME_ROLE_FAILED`, `MISSING_ROLE_ARN`) is lost.
 */
export async function extractSyncBolS3Error(
  syncError: unknown,
  syncResult: { error?: string; code?: string } | null | undefined,
): Promise<{ message: string; code?: string }> {
  // Happy-path fallback: function returned 2xx but { success: false }.
  if (!syncError && syncResult?.error) {
    return { message: syncResult.error, code: syncResult.code };
  }

  if (syncError && typeof syncError === "object") {
    const err = syncError as FunctionsHttpError & {
      context?: { response?: Response };
      message?: string;
    };
    const response = err.context?.response;
    if (response) {
      try {
        // Clone so callers (or retries) can still read the body if needed.
        const cloned = response.clone();
        const text = await cloned.text();
        if (text) {
          try {
            const parsed = JSON.parse(text) as { error?: string; code?: string };
            if (parsed?.error) {
              return { message: parsed.error, code: parsed.code };
            }
          } catch {
            // Not JSON — fall through to raw text.
          }
          return { message: text.slice(0, 500) };
        }
      } catch {
        // ignore — fall back to generic message
      }
    }
    if (err.message) {
      return { message: err.message };
    }
  }

  return { message: "S3 sync failed" };
}

/** Format `{ message, code }` for display in toasts. */
export function formatSyncBolS3Error(detail: { message: string; code?: string }): string {
  return detail.code ? `${detail.message} [${detail.code}]` : detail.message;
}
